import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTicketDepartmentVolumeReport,
  exportTicketDepartmentVolumeReport,
  clearError,
} from 'store/slices/reportSlice';
import { fetchDepartments } from 'store/slices/departmentSlice';

import PageHeader from 'components/common/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Label } from 'components/ui/label';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Building2, Ticket, TrendingUp, Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import LogDateFilter from 'components/common/LogDateFilter';
import DataTable from 'components/common/DataTable';
import { Loader2 } from 'lucide-react';
import { fetchDepartmentDropdown } from 'store/slices/commonSlice';
import FormField from 'components/common/FormField';

const TicketDepartmentVolumeReportPage = () => {
  const dispatch = useDispatch();

  const {
    ticketDepartmentVolumeReport = { data: { summary: {}, results: [] }, pagination: null },
    isLoading,
    error,
  } = useSelector((state) => state.report);

  const summary = ticketDepartmentVolumeReport.data.summary || {};
  const results = ticketDepartmentVolumeReport.data.results || [];
  const pagination = ticketDepartmentVolumeReport.pagination || {};

  // Get departments from Redux state
  const departments = useSelector((state) => state.common?.departments.items || []);
  const departmentsLoading = useSelector((state) => state.department?.isLoading || false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilters, setDateFilters] = useState({});
  const [isAdvancedFilterExpanded, setIsAdvancedFilterExpanded] = useState(false);
  
  // Advanced filter states
  const [advancedFilters, setAdvancedFilters] = useState({
    department_id: 'all',
    ticket_count_min: '',
    ticket_count_max: '',
  });

  // Calculate total pages from server response with fallback
  const totalPages = pagination.total_pages || 1;

  // Check if any advanced filters are active (excluding 'all' values)
  const hasActiveAdvancedFilters = Object.values(advancedFilters).some(value => 
    value !== '' && value !== 'all'
  );

  const refetch = useCallback(() => {
    // Build the API params object with all filters
    const apiParams = {
      page,
      page_size: pageSize,
      search: searchTerm,
      report_type: 'department_volume',
      ...dateFilters,
      // Add advanced filters with proper naming for the backend
      ...(advancedFilters.department_id !== 'all' && { department_id: advancedFilters.department_id }),
      ...(advancedFilters.ticket_count_min && { ticket_count_min: advancedFilters.ticket_count_min }),
      ...(advancedFilters.ticket_count_max && { ticket_count_max: advancedFilters.ticket_count_max }),
    };
    
    
    dispatch(fetchTicketDepartmentVolumeReport(apiParams));
  }, [dispatch, page, pageSize, searchTerm, dateFilters, advancedFilters]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Reset to page 1 when search term, page size, or filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize, dateFilters, advancedFilters]);

  // Fetch all departments on component mount
  useEffect(() => {
    dispatch(fetchDepartmentDropdown());
  }, [dispatch]);

  // --- Event Handlers ---
  const handleAdvancedFilterChange = (key, value) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      department_id: 'all',
      ticket_count_min: '',
      ticket_count_max: '',
    });
  };

  const columns = [
    {
      accessorKey: 'created_by__department__name',
      header: 'Department',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.original.created_by__department__name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'ticket_count',
      header: 'Ticket Volume',
      cell: ({ row }) => (
        <Badge variant="default" className="text-lg font-semibold">
          <Ticket className="h-4 w-4 mr-1" />
          {row.original.ticket_count}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Department Volume Report"
        description="Breakdown of ticket creation volume by department"
      />

      {/* <LogDateFilter onFilter={(f) => setDateFilters(f)} /> */}

      {/* Advanced Filters Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Advanced Filters
              {hasActiveAdvancedFilters && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveAdvancedFilters && (
                <Button variant="outline" size="sm" onClick={resetAdvancedFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setIsAdvancedFilterExpanded(!isAdvancedFilterExpanded)}
              >
                {isAdvancedFilterExpanded ? (
                  <>
                    Hide <ChevronUp className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        {isAdvancedFilterExpanded && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* <div className="space-y-2">
                <Label htmlFor="department_id">Department</Label>
                <Select
                  value={advancedFilters.department_id}
                  onValueChange={(value) => handleAdvancedFilterChange('department_id', value)}
                  disabled={departmentsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={departmentsLoading ? "Loading..." : "Select department"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {Array.isArray(departments) && departments.map((department) => (
                      <SelectItem key={department.id} value={department.id}>
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div> */}
              <FormField
  label="Department"
  name="department_id"
  type="select"
  value={advancedFilters.department_id}
  onChange={(e) => handleAdvancedFilterChange('department_id', e.target.value)}
  disabled={departmentsLoading}
  options={[
    { value: 'all', label: 'All Departments' },
    ...(Array.isArray(departments)
      ? departments.map((dep) => ({
          value: String(dep.id),
          label: dep.name,
        }))
      : []),
  ]}
  placeholder={departmentsLoading ? "Loading..." : "Select department"}
/>

              
              <div className="space-y-2">
                <Label>Ticket Count Range</Label>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Input
                      type="number"
                      placeholder="Min"
                      value={advancedFilters.ticket_count_min}
                      onChange={(e) => handleAdvancedFilterChange('ticket_count_min', e.target.value)}
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      placeholder="Max"
                      value={advancedFilters.ticket_count_max}
                      onChange={(e) => handleAdvancedFilterChange('ticket_count_max', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tickets</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pagination.count || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Departments Covered</CardTitle>
            <Building2 className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{summary.departments_covered || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Top Department</CardTitle>
            <TrendingUp className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {summary.top_department || 'N/A'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Tickets per Dept</CardTitle>
            <Ticket className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary.departments_covered > 0 
                ? Math.round(summary.total_tickets / summary.departments_covered) 
                : 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* {isLoading && results.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin mr-3" />
          <span className="text-lg">Loading department volume data...</span>
        </div>
      ) : ( */}
        <DataTable
          data={results} // Use the paginated results from the server
          columns={columns}
          totalCount={pagination.count}
          searchPlaceholder="Search by department name..."
          onSearch={setSearchTerm}
          showSearch={true}
          pageIndex={page - 1} // Convert to 0-based index for DataTable
          pageSize={pageSize}
          pageCount={totalPages} // Use total pages from server response
          onPageChange={(p) => setPage(p + 1)} // Convert back to 1-based index
          onPageSizeChange={(newSize) => setPageSize(newSize)} // Page reset is handled by useEffect
          isLoading={isLoading}
          onExport={() =>
            dispatch(exportTicketDepartmentVolumeReport({ 
              filters: { 
                page,
                page_size: pageSize,
                search: searchTerm,
                report_type: 'department_volume',
                ...dateFilters,
                // Include advanced filters in export
                ...(advancedFilters.department_id !== 'all' && { department_id: advancedFilters.department_id }),
                ...(advancedFilters.ticket_count_min && { ticket_count_min: advancedFilters.ticket_count_min }),
                ...(advancedFilters.ticket_count_max && { ticket_count_max: advancedFilters.ticket_count_max }),
              } 
            }))
          }
          showExport={true}
          showImport={false}
        />
      {/* )} */}
    </div>
  );
};

export default TicketDepartmentVolumeReportPage;